// API Service Layer for FitPro Hub
import { saveToken, getToken, clearToken } from '../utils/tokenStorage';

// Use environment variable if set, otherwise use relative URL (works for same-origin deployment)
const API_URL = '';

// Request options interface
interface RequestOptions {
  method?: string;
  body?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

// Response data types
interface AuthResponse {
  token?: string;
  user?: {
    id?: string;
    user_id?: string;
    email?: string;
    name?: string;
    [key: string]: unknown;
  };
  requires_2fa?: boolean;
  temp_token?: string;
  email_hint?: string;
  device_verification?: boolean;
  message?: string;
}

interface ApiResponse<T = unknown> {
  data?: T;
  detail?: string;
  message?: string;
  [key: string]: unknown;
}

// Subscription related types
interface PlanInfo {
  id: string;
  name: string;
  price: number;
  interval: string;
  trial_days?: number;
}

interface PlansResponse {
  plans: PlanInfo[];
  currencies: string[];
}

interface CheckoutResponse {
  checkout_url: string;
  checkout_id?: string;
}

class ApiService {
  private token: string | null = null;

  constructor() {
    // Token is now managed via httpOnly cookies set by the server.
    // Keep in-memory token as fallback for backwards compatibility only.
    try {
      this.token = getToken();
    } catch (e) {
      // localStorage not available
    }
  }

  setToken(token: string | null): void {
    this.token = token;
    try {
      if (token) {
        saveToken(token);
      } else {
        clearToken();
      }
    } catch (e) {
      // localStorage not available
    }
  }

  getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    // Include device ID for 2FA trusted device checks
    try {
      const deviceId = localStorage.getItem('device_id');
      if (deviceId) headers['X-Device-ID'] = deviceId;
    } catch { /* noop */ }
    return headers;
  }

  async request<T = ApiResponse>(endpoint: string, options: RequestOptions = {}, retryCount: number = 0): Promise<T> {
    // Build URL: use API_URL if set, otherwise use relative path
    const url = API_URL ? `${API_URL}${endpoint}` : endpoint;
    const config: RequestInit = {
      ...options,
      headers: this.getHeaders(),
      credentials: 'include' as RequestCredentials,
    };

    try {
      // Add timeout to prevent hanging requests
      // Use longer timeout for AI endpoints (2 minutes)
      const isAiEndpoint = url.includes('/ai/') || url.includes('ai-generate');
      const timeoutMs = isAiEndpoint ? 120000 : 30000;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      let response: Response;
      try {
        response = await fetch(url, { ...config, signal: controller.signal });
      } finally {
        clearTimeout(timeoutId);
      }
      
      // Get response status before body is consumed
      const status = response.status;
      const ok = response.ok;
      
      // Try to read response body - simpler approach
      let responseText = '';
      let data: T | null = null;
      
      try {
        responseText = await response.text();
      } catch (textErr) {
        console.error(`Failed to read response body [${endpoint}]:`, textErr);
        if (!ok) {
          throw new Error(`Request failed (${status})`);
        }
        return {} as T;
      }
      
      // Debug log - show more for AI endpoints
      if (isAiEndpoint) {
        console.log(`AI API Response [${endpoint}]: status=${status}, ok=${ok}, length=${responseText.length}`);
      } else {
        console.log(`API Response [${endpoint}]:`, responseText.substring(0, 200));
      }
      
      // Parse JSON if we have content
      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText) as T;
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          if (!ok) {
            throw new Error(responseText.length < 100 ? responseText : 'Server error. Please try again.');
          }
        }
      }
      
      // Handle 401 Unauthorized
      if (status === 401) {
        const errorData = data as ApiResponse;
        const errorMessage = String(errorData?.detail || errorData?.message || 'Authentication failed. Please try again.');
        
        // NEVER auto-clear tokens — the 401 might be a race condition right after login.
        // Let AuthContext handle genuine session expiration through its own periodic check.
        // Just throw the error so the caller can handle it gracefully.
        
        throw new Error(errorMessage);
      }
      
      // Handle 403 Subscription Expired — dispatch event so UI can show paywall
      if (status === 403) {
        const errorData = data as ApiResponse;
        const detail = String(errorData?.detail || '');
        if (detail.toLowerCase().includes('subscription')) {
          window.dispatchEvent(new CustomEvent('subscription-expired'));
        }
        throw new Error(detail || 'Access denied');
      }
      
      // Handle 5xx errors with retry
      if (status >= 500 && status < 600 && retryCount < 2) {
        console.log(`Server error ${status}, retrying (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.request<T>(endpoint, options, retryCount + 1);
      }
      
      // Handle other error status codes
      if (!ok) {
        const errorData = data as ApiResponse;
        if (status >= 500) {
          throw new Error(`Server error (${status}). Please try again in a moment.`);
        }
        throw new Error(errorData?.detail || errorData?.message || 'Request failed');
      }
      
      // For successful empty responses (like logout), return empty object
      if (!data && ok) {
        return {} as T;
      }
      
      return data as T;
    } catch (error) {
      const err = error as Error;
      console.error(`API Error [${endpoint}]:`, err);
      
      // Retry on network errors
      if ((err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) && retryCount < 2) {
        console.log(`Network error, retrying (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.request<T>(endpoint, options, retryCount + 1);
      }
      
      // Improve error messages for common issues
      if (err.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw error;
    }
  }

  // Generic HTTP methods
  async get<T = ApiResponse>(endpoint: string): Promise<T> {
    return this.request<T>(`/api${endpoint}`, {
      method: 'GET',
    });
  }

  async post<T = ApiResponse>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(`/api${endpoint}`, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = ApiResponse>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(`/api${endpoint}`, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = ApiResponse>(endpoint: string): Promise<T> {
    return this.request<T>(`/api${endpoint}`, {
      method: 'DELETE',
    });
  }

  // Auth endpoints
  async register(name: string, email: string, password: string, accountType: 'user' | 'trainer' = 'user'): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, account_type: accountType }),
    });
    // Save token after successful registration
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const data = await this.request<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async processGoogleSession(sessionId: string): Promise<AuthResponse> {
    console.log('[API] Processing Google session, API_URL:', API_URL);
    console.log('[API] Full endpoint:', `${API_URL}/api/auth/session?session_id=${sessionId.substring(0, 10)}...`);
    
    try {
      const data = await this.request<AuthResponse>(`/api/auth/session?session_id=${sessionId}`);
      console.log('[API] Session processed successfully');
      if (data.token) {
        this.setToken(data.token);
      }
      return data;
    } catch (err) {
      const error = err as Error;
      console.error('[API] Session processing failed:', error.message);
      throw err;
    }
  }

  async getMe(): Promise<AuthResponse> {
    return this.request<AuthResponse>('/api/auth/me');
  }

  async logout(): Promise<void> {
    try {
      await this.request('/api/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  // Subscription endpoints
  async getPlans(currency: string = 'EUR'): Promise<PlansResponse> {
    return this.request<PlansResponse>(`/api/subscription/plans?currency=${currency}`);
  }

  async createCheckout(plan: string, currency: string = 'EUR'): Promise<CheckoutResponse> {
    return this.request<CheckoutResponse>('/api/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, currency }),
    });
  }

  async getPaymentHistory(): Promise<ApiResponse> {
    return this.request('/api/subscription/history');
  }

  // Sync endpoints
  async uploadSyncData(data: unknown): Promise<ApiResponse> {
    return this.request('/api/sync/upload', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async downloadSyncData(): Promise<ApiResponse> {
    return this.request('/api/sync/download');
  }

  async getSyncStatus(): Promise<ApiResponse> {
    return this.request('/api/sync/status');
  }

  // Profile endpoints
  async updateProfile(name: string): Promise<ApiResponse> {
    return this.request(`/api/user/profile?name=${encodeURIComponent(name)}`, {
      method: 'PUT',
    });
  }

  // Training History endpoints
  async deleteTrainingHistoryEntry(entryId: string): Promise<ApiResponse> {
    return this.request(`/api/data/training-history/${entryId}`, {
      method: 'DELETE',
    });
  }

  async clearTrainingHistory(): Promise<ApiResponse> {
    return this.request('/api/data/training-history', {
      method: 'DELETE',
    });
  }

  // Invoice endpoint
  async downloadInvoice(paymentId: string): Promise<Blob> {
    const url = API_URL ? `${API_URL}/api/invoice/${paymentId}` : `/api/invoice/${paymentId}`;
    const response = await fetch(url, {
      headers: this.getHeaders(),
      credentials: 'include' as RequestCredentials,
    });
    
    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }
    
    const blob = await response.blob();
    return blob;
  }
}

export const api = new ApiService();
export default api;
